import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  formatCurrency,
  getCreditSituationColor,
  getCreditTypeBreakdown,
  normalizeCreditSituation,
  parseNumericValue,
} from '../utils/caseDisplayUtils';
import { Calculate as CalculateIcon } from '@mui/icons-material';
import { calculateEndeudamiento, TAE_FORMULARIO } from '../utils/endeudamiento';
import { auth } from '../shared/firebase';
import { useWaGroups } from '../hooks/useWaGroups';
import { canUseCalculator } from '../utils/calculatorAccess';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography
      variant="h6"
      color="primary"
      gutterBottom
      sx={{
        fontWeight: 'bold',
        borderBottom: '2px solid var(--ed-line, #e9ecef)',
        pb: 1,
        mb: 2,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const formatPercentage = (value) => {
  const numericValue = parseNumericValue(value);
  return `${numericValue.toFixed(2)}%`;
};

const formatDate = (dateString) => {
  if (!dateString) return '0';
  if (dateString.includes('/')) return dateString;

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;

    return date.toLocaleDateString('es-ES', { month: '2-digit', year: 'numeric' });
  } catch {
    return dateString;
  }
};

const formatCalculatedCurrency = (value) => (
  Number.isFinite(value)
    ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value)
    : '—'
);

const formatCalculatedPercentage = (value) => (
  Number.isFinite(value)
    ? `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 1 }).format(value)} %`
    : '—'
);

const ratioColor = (value) => {
  if (!Number.isFinite(value)) return 'text.primary';
  if (value > 50) return 'error.main';
  if (value > 45) return 'warning.main';
  return 'text.primary';
};

const EndeudamientoSummary = ({ creditos, record = {} }) => {
  const calculator = {
    honorarios: '',
    plazoMeses: 120,
    ...(record.calculadoraEndeudamiento || {}),
    tasaAnual: TAE_FORMULARIO,
  };
  const [seguroElegido, setSeguroElegido] = useState(null);
  const incluirSeguro = seguroElegido ?? !!calculator.incluirSeguro;
  const calculo = calculateEndeudamiento({
    creditos,
    primerTitular: record.primerTitular,
    segundoTitular: record.segundoTitular,
    gastosVivienda: record.gastosVivienda,
    otrosDatos: record.otrosDatos,
    honorarios: calculator.honorarios,
    plazoMeses: calculator.plazoMeses,
    tasaAnual: calculator.tasaAnual,
    incluirSeguro,
  });

  return (
    <Card sx={{ mt: 3, border: '1px solid', borderColor: 'primary.light' }}>
      <CardContent>
        <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalculateIcon /> Calculadora de endeudamiento
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Estimación automática con una TAE del {formatCalculatedPercentage(calculo.tasaAnual)}. Los créditos de la tabla se reúnen en la nueva oferta.
        </Typography>

        <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
          General
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Ingresos mensuales</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.ingresos)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Hipoteca / alquiler</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.vivienda)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Otras cuotas fuera de la tabla</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.otrasCuotas)}</Typography>
          </Grid>
        </Grid>

        <Box sx={{ my: 2, borderTop: '1px solid', borderColor: 'divider' }} />
        <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
          Situación actual
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Cuotas de los créditos de la tabla</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.cuotaCreditos)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Cuota actual total</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.cuotaActual)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Ratio actual</Typography>
            <Typography variant="body1" fontWeight={600} sx={{ color: ratioColor(calculo.ratioActual) }}>
              {formatCalculatedPercentage(calculo.ratioActual)}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ my: 2, borderTop: '1px solid', borderColor: 'divider' }} />
        <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
          Situación nueva
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Honorarios a financiar</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.honorarios)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Plazo (meses)</Typography>
            <Typography variant="body1" fontWeight={600}>{calculo.plazoMeses}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">TAE</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedPercentage(calculo.tasaAnual)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Capital a reunificar (+1 %)</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.capitalAReunificar)}</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={incluirSeguro} onChange={(e) => setSeguroElegido(e.target.checked)} />}
              label="Incluir seguro"
            />
            <Typography variant="body2" color="textSecondary">
              Seguro mensual: {formatCalculatedCurrency(calculo.importeSeguro)}. Se calcula sobre el capital con el 1 % y los honorarios incluidos y se suma a la cuota.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Cuota sin seguro</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.cuotaSinSeguro)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Cuota con seguro</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.cuotaConSeguro)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Total a financiar</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.capitalFinanciado)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Nueva cuota</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.cuotaNueva)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Diferencia frente a la cuota de la tabla</Typography>
            <Typography variant="body1" fontWeight={600}>{formatCalculatedCurrency(calculo.diferenciaCuotas)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Nuevo ratio de endeudamiento</Typography>
            <Typography variant="body1" fontWeight={600} sx={{ color: ratioColor(calculo.ratioNuevo) }}>
              {formatCalculatedPercentage(calculo.ratioNuevo)}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const AdminCreditInformation = ({ creditos, record }) => {
  const waGroups = useWaGroups();
  const showCalculator = canUseCalculator(auth.currentUser?.email, waGroups);
  const safeCreditos = Array.isArray(creditos) ? creditos : [];

  const normalizedCreditos = safeCreditos.map((credit) => ({
    ...credit,
    cuotaMensual: credit.cuotaMensual || credit.pagoMensual || '',
    capitalInicial: credit.capitalInicial || '',
    tae: credit.tae || '',
    porcentajeCierre: credit.porcentajeCierre || '',
    fechaFinCredito: credit.fechaFinCredito || '',
    contratoTarjeta: credit.contratoTarjeta || '',
    situacion: normalizeCreditSituation(credit.situacion),
  }));

  const totalCapitalInicial = normalizedCreditos.reduce(
    (sum, credit) => sum + parseNumericValue(credit.capitalInicial),
    0
  );
  const totalCapitalPendiente = normalizedCreditos.reduce(
    (sum, credit) => sum + parseNumericValue(credit.capitalPendiente),
    0
  );
  const totalCuotaMensual = normalizedCreditos.reduce(
    (sum, credit) => sum + parseNumericValue(credit.cuotaMensual),
    0
  );
  const creditTypeBreakdown = getCreditTypeBreakdown(normalizedCreditos);

  return (
    <Box>
      <Section title="INFORMACIÓN DE CRÉDITOS">
        {safeCreditos.length > 0 ? <>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer component={Paper} sx={{ mb: 2, overflowX: 'auto' }}>
            <Table sx={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'var(--ed-soft, #f8f9fa)' }}>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>
                    Banco / Financiera
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 130, whiteSpace: 'nowrap' }}>
                    Tipo crédito
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120, whiteSpace: 'nowrap' }}>
                    Capital Inicial
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120, whiteSpace: 'nowrap' }}>
                    Capital pendiente
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120, whiteSpace: 'nowrap' }}>
                    Cuota Mensual
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 80, whiteSpace: 'nowrap' }}>
                    TAE
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 80, whiteSpace: 'nowrap' }}>
                    % Cierre
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>
                    Fecha Solicitud
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>
                    Fecha fin crédito
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120, whiteSpace: 'nowrap' }}>
                    CONTRATO O TARJETA
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>
                    Situación
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {normalizedCreditos.map((credit, index) => (
                  <TableRow
                    key={credit.id || index}
                    sx={{ '&:nth-of-type(even)': { backgroundColor: 'var(--ed-soft, #f8f9fa)' } }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                      {credit.banco || '-'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{credit.tipoCredito || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 500 }}>
                      {formatCurrency(credit.capitalInicial)}
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        textAlign: 'right',
                        fontWeight: 500,
                        color: 'warning.main',
                      }}
                    >
                      {formatCurrency(credit.capitalPendiente)}
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        textAlign: 'right',
                        fontWeight: 500,
                        color: 'success.main',
                      }}
                    >
                      {formatCurrency(credit.cuotaMensual)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                      {formatPercentage(credit.tae)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                      {formatPercentage(credit.porcentajeCierre)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                      {formatDate(credit.fechaSolicitud)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                      {formatDate(credit.fechaFinCredito)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                      {credit.contratoTarjeta || 'NA'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        label={credit.situacion || '-'}
                        size="small"
                        color={getCreditSituationColor(credit.situacion)}
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {normalizedCreditos.map((credit, index) => (
            <Card key={credit.id || index} sx={{ mb: 2, p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Banco
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {credit.banco || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Tipo
                  </Typography>
                  <Typography variant="body1">{credit.tipoCredito || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Capital Inicial
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatCurrency(credit.capitalInicial)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Capital Pendiente
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'warning.main' }}>
                    {formatCurrency(credit.capitalPendiente)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Cuota Mensual
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main' }}>
                    {formatCurrency(credit.cuotaMensual)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    TAE
                  </Typography>
                  <Typography variant="body1">{formatPercentage(credit.tae)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    % Cierre
                  </Typography>
                  <Typography variant="body1">{formatPercentage(credit.porcentajeCierre)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Fecha Solicitud
                  </Typography>
                  <Typography variant="body1">{formatDate(credit.fechaSolicitud)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Fecha fin
                  </Typography>
                  <Typography variant="body1">{formatDate(credit.fechaFinCredito)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Contrato/Tarjeta
                  </Typography>
                  <Typography variant="body1">{credit.contratoTarjeta || 'NA'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Situación
                  </Typography>
                  <Chip
                    label={credit.situacion || '-'}
                    size="small"
                    color={getCreditSituationColor(credit.situacion)}
                    sx={{ fontSize: '0.75rem' }}
                  />
                </Grid>
              </Grid>
            </Card>
          ))}
        </Box>

        <Card sx={{ mt: 2, bgcolor: 'var(--ed-info-soft, #e3f2fd)' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Resumen de Créditos
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Capital Inicial
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(totalCapitalInicial)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Capital Pendiente
                </Typography>
                <Typography variant="h6" color="warning.main">
                  {formatCurrency(totalCapitalPendiente)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Cuota Mensual
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(totalCuotaMensual)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Número de Créditos
                </Typography>
                <Typography variant="h6" color="info.main">
                  {normalizedCreditos.length}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {creditTypeBreakdown.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Capital pendiente por tipo de crédito
            </Typography>
            <Grid container spacing={2}>
              {creditTypeBreakdown.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.key}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      {item.label}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: item.highlightInRed ? 'error.main' : 'text.primary',
                      }}
                    >
                      {formatCurrency(item.total)}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        </> : (
          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
            No hay información de créditos disponible
          </Typography>
        )}
      </Section>
      {showCalculator && <EndeudamientoSummary key={record?.id} creditos={normalizedCreditos} record={record} />}
    </Box>
  );
};

export default AdminCreditInformation;
